import { customElement, property } from 'lit/decorators.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { classMap } from 'lit/directives/class-map.js';
import { CSSResultGroup, html, TemplateResult } from 'lit';
import componentStyles from './tufts-infographic.css.lit';
import { SlotNGController } from '../../controllers/slot-ng-controller';
import '../tufts-heading/tufts-heading';
import '../tufts-container/tufts-container';

import { ResizeController } from '../../controllers/resize-controller';
import dataBubbleStyles from '../tufts-data-bubble/tufts-data-bubble.css.lit';
/**
 * The Tufts Infographic Section
 * @element tufts-infographic
 *
 * @slot title
 * @attr background-color same as container background-color
 */

@customElement('tufts-infographic')
export class tuftsInfographic extends OutlineElement {
  ngSlot = new SlotNGController(this);
  static styles: CSSResultGroup = [componentStyles, dataBubbleStyles];
  resizeController: ResizeController = new ResizeController(this, {
    breakpoints: [1024],
  });

  isMobile: boolean;
  hasDataSlot2: boolean;
  hasDataSlot3: boolean;

  @property({
    type: String,
    reflect: true,
    attribute: 'bg-color',
  })
  bgColor = 'white';

  @property({
    type: String,
    reflect: true,
    attribute: 'variation',
  })
  variation = 'two-column';

  @property({
    type: String,
    reflect: true,
    attribute: 'shape',
  })
  shape = 'circle';

  firstUpdated(): void {
    this.hasDataSlot2 = this.ngSlot.exist('data-second');
    this.hasDataSlot3 = this.ngSlot.exist('data-third');
  }

  /**
   * Check variation
   */
  columnNumber() {
    if (this.variation == 'two-column') {
      return true;
    }
    return false;
  }

  isBgWhite() {
    if (
      this.bgColor == 'white' &&
      this.getParentContainerBackgroundColor() === 'white'
    ) {
      return true;
    }
    return false;
  }

  /**
   * Gets the background-color attribute of the closest tufts-container ancestor
   * @returns The background-color attribute value or 'white' as default
   */
  getParentContainerBackgroundColor(): string {
    // get the parent node component
    const parentElement = this.closest('tufts-container') as Element;
    // if a parent element exists, check what the value of background-color is
    if (parentElement) {
      return parentElement.getAttribute('background-color') || 'white';
    }
    return 'white';
  }
  twoColumnTemplate(): TemplateResult {
    this.isMobile = this.resizeController.currentBreakpointRange === 0;
    const wrapperClasses = {
      'two-column-wrapper': true,
      'mobile': this.isMobile,
    };
    const fullWrapperClasses = {
      'full-wrapper': true,
      'mobile': this.isMobile,
      'background-color': this.isBgWhite(),
    };
    return html`
      <div class=${classMap(fullWrapperClasses)}>
        <tufts-heading level-size="lg"
          >${this.ngSlot.conditionalNGSlot('title')}</tufts-heading
        >
        <div class=${classMap(wrapperClasses)}>
          <div class="main-content">
            <div class="data-bubble ${this.bgColor} ${this.shape}">
              ${this.ngSlot.conditionalNGSlot('data')}
              ${this.ngSlot.conditionalNGSlot('description')}
              ${this.ngSlot.conditionalNGSlot('label')}
            </div>
            ${this.hasDataSlot2
              ? html`<div class="data-bubble ${this.bgColor} ${this.shape}">
                  ${this.ngSlot.conditionalNGSlot('data-second')}
                  ${this.ngSlot.conditionalNGSlot('description-second')}
                  ${this.ngSlot.conditionalNGSlot('label-second')}
                </div>`
              : null}
          </div>
          <div class="secondary-content">
            ${this.ngSlot.conditionalNGSlot('details')}
          </div>
        </div>
      </div>
    `;
  }

  threeColumnTemplate(): TemplateResult {
    this.isMobile = this.resizeController.currentBreakpointRange === 0;
    const wrapperClasses = {
      'three-column-wrapper': true,
      'mobile': this.isMobile,
    };
    const fullWrapperClasses = {
      'full-wrapper': true,
      'mobile': this.isMobile,
      'background-color': this.isBgWhite(),
    };
    return html`
      <div class=${classMap(fullWrapperClasses)}>
        <tufts-heading level-size="xl"
          >${this.ngSlot.conditionalNGSlot('title')}</tufts-heading
        >
        <div class=${classMap(wrapperClasses)}>
          <div class="data-bubble ${this.bgColor} ${this.shape}">
            ${this.ngSlot.conditionalNGSlot('data')}
            ${this.ngSlot.conditionalNGSlot('description')}
            ${this.ngSlot.conditionalNGSlot('label')}
          </div>
          <div class="data-bubble ${this.bgColor} ${this.shape}">
            ${this.ngSlot.conditionalNGSlot('data-second')}
            ${this.ngSlot.conditionalNGSlot('description-second')}
            ${this.ngSlot.conditionalNGSlot('label-second')}
          </div>
          ${this.hasDataSlot3
            ? html`<div class="data-bubble ${this.bgColor} ${this.shape}">
                ${this.ngSlot.conditionalNGSlot('data-third')}
                ${this.ngSlot.conditionalNGSlot('description-third')}
                ${this.ngSlot.conditionalNGSlot('label-third')}
              </div>`
            : null}
          ${this.ngSlot.conditionalNGSlot('details')}
        </div>
      </div>
    `;
  }

  render() {
    return html`
      ${this.columnNumber()
        ? this.twoColumnTemplate()
        : this.threeColumnTemplate()}
    `;
  }
}
